import { Link, useNavigate } from "@tanstack/react-router";
import { BookOpen, Globe, LogOut, Menu, MessageCircle, Plus, Search, BookHeart, Target, User as UserIcon, Library, BookMarked, Users, Gift, Heart, Star, History as HistoryIcon, ArrowLeftRight } from "lucide-react";
// Chat icon removed from top nav per UX spec — access chats via notifications or Menu.
import { useAuth } from "@/lib/auth-context";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { ThemeToggle } from "@/components/ThemeToggle";
import { NotificationBell } from "@/components/NotificationBell";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export function Navbar() {
  const { user, signOut } = useAuth();
  const { t, lang, setLang } = useI18n();
  const navigate = useNavigate();

  return (
    <header className="sticky top-0 z-40 border-b border-border bg-primary-dark text-white shadow-md dark:bg-card dark:text-foreground">
      <nav className="mx-auto flex max-w-6xl items-center justify-between gap-1 px-3 py-3 sm:gap-2 sm:px-4">
        <div className="flex min-w-0 items-center gap-1 sm:gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="ghost" className="gap-2 text-white/90 hover:bg-white/10 hover:text-white dark:text-foreground dark:hover:bg-muted" aria-label={t("nav.menu")}>
                <Menu className="h-4 w-4" />
                <span className="hidden sm:inline">{t("nav.menu")}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-52">
              <DropdownMenuLabel>{t("nav.menu")}</DropdownMenuLabel>
              <DropdownMenuItem asChild>
                <Link to="/browse" className="flex w-full items-center gap-2"><Search className="h-4 w-4" /> {t("nav.browse")}</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/add-book" className="flex w-full items-center gap-2"><Plus className="h-4 w-4" /> {t("nav.add")}</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/my-books" className="flex w-full items-center gap-2"><Library className="h-4 w-4" /> {t("nav.my_books")}</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/exchanges" className="flex w-full items-center gap-2"><ArrowLeftRight className="h-4 w-4" /> Chosen books</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/history" className="flex w-full items-center gap-2"><HistoryIcon className="h-4 w-4" /> Exchange history</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/shelves" className="flex w-full items-center gap-2"><Library className="h-4 w-4" /> Family shelves</Link>
              </DropdownMenuItem>
              {user && (
                <>
                  <DropdownMenuItem asChild>
                    <Link to="/recommendations" className="flex w-full items-center gap-2"><BookHeart className="h-4 w-4" /> {t("nav.recommendations")}</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/missions" className="flex w-full items-center gap-2"><Target className="h-4 w-4" /> Reading missions</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/clubs" className="flex w-full items-center gap-2"><Users className="h-4 w-4" /> Reading clubs</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/feed" className="flex w-full items-center gap-2"><Heart className="h-4 w-4" /> Activity feed</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/journey" className="flex w-full items-center gap-2"><BookMarked className="h-4 w-4" /> My reading journey</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/invite" className="flex w-full items-center gap-2"><Gift className="h-4 w-4" /> Invite a friend</Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/messages" className="flex w-full items-center gap-2"><MessageCircle className="h-4 w-4" /> {t("nav.messages")}</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/profile" className="flex w-full items-center gap-2"><UserIcon className="h-4 w-4" /> {t("nav.profile")}</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/rate-app" className="flex w-full items-center gap-2"><Star className="h-4 w-4" /> {t("Rate the app")}</Link>
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          <Link to="/" className="flex min-w-0 items-center gap-2 text-white/90 dark:text-foreground">
            <BookOpen className="h-6 w-6 shrink-0" />
            <span className="truncate whitespace-nowrap text-base font-bold tracking-tight sm:text-lg">Book Swap</span>
          </Link>
        </div>

        <div className="flex shrink-0 items-center gap-0.5 sm:gap-2">
          <NotificationBell />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="ghost" className="px-2 text-white/90 hover:bg-white/10 hover:text-white dark:text-foreground dark:hover:bg-muted" aria-label={t("nav.language")}>
                <Globe className="h-4 w-4" />
                <span className="ms-1 hidden text-xs font-semibold uppercase sm:inline">{lang}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>{t("nav.language")}</DropdownMenuLabel>
              <DropdownMenuItem onClick={() => setLang("en")}>English {lang === "en" && "✓"}</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setLang("ar")}>العربية {lang === "ar" && "✓"}</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <ThemeToggle />
          {user ? (
            <Button size="sm" variant="ghost" className="px-2 text-white/90 hover:bg-white/10 hover:text-white dark:text-foreground dark:hover:bg-muted"
              onClick={async () => { await signOut(); navigate({ to: "/" }); }} aria-label={t("nav.signout")}>
              <LogOut className="h-4 w-4" />
            </Button>
          ) : (
            <>
              <Link to="/login" className="hidden sm:inline-flex"><Button size="sm" variant="ghost" className="text-white/90 hover:bg-white/10 hover:text-white dark:text-foreground dark:hover:bg-muted">{t("nav.signin")}</Button></Link>
              <Link to="/register"><Button size="sm" className="whitespace-nowrap bg-white px-3 text-primary-dark hover:bg-white/90 dark:bg-primary dark:text-primary-foreground dark:hover:bg-primary/90">{t("nav.get_started")}</Button></Link>
            </>
          )}
        </div>
      </nav>
    </header>
  );
}
